/* ────────────────────────────────────────────────
   src/components/HeroSolarAnimation.jsx
───────────────────────────────────────────────── */
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  useTheme,
  alpha,
  useMediaQuery,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

import heroBg1 from "../assets/paneles-fondo-oscuro.png";
import heroBg2 from "../assets/paneles-campo.png";

export default function HeroSolarAnimation() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  /* ─────── carrusel simple ─────── */
  const images = [heroBg1, heroBg2];
  const INTERVAL = 12_000; // espera entre cambios
  const SPEED = 3.5; // duración del slide

  const [[index, dir], setIndex] = useState([0, 1]);

  useEffect(() => {
    const id = setInterval(
      () => setIndex(([i]) => [(i + 1) % images.length, 1]),
      INTERVAL
    );
    return () => clearInterval(id);
  }, []);

  /* variantes */
  const slide = {
    enter: (d) => ({ x: d > 0 ? "100%" : "-100%" }),
    center: {
      x: 0,
      transition: { duration: SPEED, ease: [0.25, 0.8, 0.25, 1] },
    },
    exit: (d) => ({
      x: d > 0 ? "-100%" : "100%",
      transition: { duration: SPEED, ease: [0.25, 0.8, 0.25, 1] },
    }),
  };

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        height: "100dvh",
        display: "flex",
        alignItems: "center",
        px: { xs: 3, md: 10 },
        overflow: "hidden",
        /* 👉 color de fondo igual al velo por si el navegador
              llega a pintar antes de tiempo */
        bgcolor: "rgba(0,0,0,.55)",
      }}
    >
      {/* ───── Fondo deslizante ───── */}
      <AnimatePresence initial={false} custom={dir}>
        <motion.img
          key={index}
          src={images[index]}
          custom={dir}
          variants={slide}
          initial="enter"
          animate="center"
          exit="exit"
          draggable={false}
          style={{
            position: "absolute",
            top: "-1px", // ← se extiende 1 px
            left: "-1px",
            width: "calc(100% + 2px)",
            height: "calc(100% + 2px)",
            objectFit: "cover",
            userSelect: "none",
            pointerEvents: "none",
            willChange: "transform",
          }}
        />
      </AnimatePresence>

      {/* velo permanente */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(0,0,0,.55)",
          pointerEvents: "none",
        }}
      />

      {/* ───── Contenido ───── */}
      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 820 }}>
        <Typography
          component={motion.h1}
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          sx={{
            fontFamily: '"Ethos Nova", sans-serif',
            fontWeight: 900,
            lineHeight: 1.1,
            fontSize: { xs: 36, sm: 46, md: 62, lg: 70 },
            color: "#fff",
            mb: 3,
          }}
        >
          <span style={{ color: theme.palette.primary.main }}>Ahorra</span>
          &nbsp;hasta&nbsp;95&nbsp;% en tu recibo de luz con&nbsp;
          <span style={{ color: theme.palette.primary.main }}>KWSOLAR</span>
        </Typography>

        <Typography
          variant="body1"
          color={alpha("#FFFFFF", 0.93)}
          maxWidth={640}
          mb={5}
          sx={{
            fontFamily: '"Ethos Nova", sans-serif',
            fontStyle: "italic",
            fontWeight: 900,
            fontSize: { xs: 18, sm: 21 },
            lineHeight: 1.7,
            textShadow: "0 1px 2px rgba(0,0,0,.25)",
          }}
        >
          Transforma tu consumo energético y contribuye a un futuro sostenible —
          sin enganche y con retorno en &lt;&nbsp;4&nbsp;años.
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <motion.div
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
          >
            <Button
              component={motion.a}
              href="/cotizar"
              size="large"
              variant="contained"
              fullWidth={isXs}
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.96 }}
              sx={{
                py: 2,
                px: 6,
                fontWeight: 700,
                textTransform: "none",
                borderRadius: 3,
                backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 0 14px ${alpha(
                  theme.palette.primary.main,
                  0.45
                )}`,
              }}
            >
              Cotizar ahora
            </Button>
          </motion.div>
        </Stack>
      </Box>
    </Box>
  );
}
